import { useCallback, useDebugValue, useEffect, useMemo, useRef, useState } from 'react';
import { useUnmount } from 'react-use';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { WebSocketStatus } from '../tokens/socket';

import { v4 as uuid } from 'uuid';
import { useConstant } from './useConstant';
import { useDynamicCallback } from './useDynamicCallback';

import { useSocketClient, useSocketStatus } from '../providers/WebSocketClientProvider';
import type { RequestStream } from '../types/RequestStream';

import type { SubscriptionCallback } from '../providers';
import {
  isErrorSubscriptionResponse,
  type ErrorSubscriptionResponse,
  type SubscriptionResponse,
} from '../types/SubscriptionResponse';
import { usePageVisibility } from './usePageVisibility';
export interface NextPageOptions {
  loadAll?: boolean;
}

export interface UseSubscriptionReturn<T> {
  isLoading: boolean;
  data: Observable<T>;
  error: Observable<ErrorSubscriptionResponse | undefined>;
  /**
   * Push a manual update to the stream.
   * Can be used for example to perform kinds of optimistic updates, or just push in REST operation success results for immediate frontend feedback.
   * If you are pushing manual updates and co-mingling that with backend updates, make sure your pipeline is idempotent.
   */
  push: (message: T) => void;
  nextPage: (options?: NextPageOptions) => void;
}

export type UseSubscriptionRequest = RequestStream | RequestStream[] | null;

export interface UseSubscriptionOptions {
  replay?: boolean;
  loadAll?: boolean;
  overrideParticipant?: string;
  unsubscribeOnPageInvisibility?: boolean;
  /** Route this subscription through a client registered via `useRegisterSocketClient` (e.g. `'cm'`
   * for CoinMetrics) instead of the primary Talos client. */
  client?: string;
}

/** Subscribe to Ava Service endpoints via the given requests
 * @param request The request to subscribe to; if set to null, the subscription will be unsubscribed (or won't be started)
 * @param options Options for the subscription
 * @returns An object containing the subscription state
 */
export function useSubscription<TResponse extends SubscriptionResponse<any, any>>(
  request: UseSubscriptionRequest,
  options?: UseSubscriptionOptions
): UseSubscriptionReturn<TResponse>;
export function useSubscription<TData>(
  request: UseSubscriptionRequest,
  options?: UseSubscriptionOptions
): UseSubscriptionReturn<SubscriptionResponse<TData>>;
export function useSubscription<T = any>(
  request: UseSubscriptionRequest,
  { replay = true, ...options }: UseSubscriptionOptions = {}
): UseSubscriptionReturn<T> {
  const address = useConstant(uuid());

  // Must keep track of request so we know when to update subscription
  const activeRequest = useRef<RequestStream[] | null>();

  // Must also keep track of if we have an RxJS subscriber.
  // If not, there's no point in performing the socket subscription.
  const [subscriberCount, setSubscriberCount] = useState(0);
  const hasSubscriber = subscriberCount > 0;

  // Keep track of the current isLoading state.
  const [isLoading, setIsLoading] = useState(false);

  const client = useSocketClient(options.client);
  const status = useSocketStatus(options.client);
  // Tracks the client instance our active registration is actually held against, independent of the
  // request's own content. A client can be replaced outright under the same key (e.g. a
  // CoinMetricsWebSocketClient recreated after an API key rotation) — the replacement starts with no
  // knowledge of any prior registration even when the request itself never changed, so a swap must be
  // treated as "no active registration" rather than a no-op (see the effect below).
  const registeredClientRef = useRef(client);

  const isPageVisible = usePageVisibility();

  const subject = useMemo(() => {
    return replay ? new ReplaySubject<SubscriptionResponse>(1) : new Subject<SubscriptionResponse>();
  }, [replay]);

  const errors = useConstant(new ReplaySubject<ErrorSubscriptionResponse | undefined>(1));

  const observable = useMemo(
    () =>
      new Observable(subscriber => {
        const subscription = subject.asObservable().subscribe(x => {
          // Delta messages can arrive while the initial response is being paged. They do not have a
          // `next` marker, so only update the loading state from the initial response or a page response.
          if (!options.loadAll) {
            setIsLoading(false);
          } else if (x.initial || x.page) {
            setIsLoading(Boolean(x.next));
          }
          subscriber.next(x);
        });
        setSubscriberCount(curr => curr + 1);
        return () => {
          subscription.unsubscribe();
          setSubscriberCount(curr => curr - 1);
        };
      }),
    [subject, options.loadAll]
  );

  const errorObservable = useMemo(
    () =>
      new Observable(subscriber => {
        const subscription = errors.asObservable().subscribe(x => {
          if (x != null) {
            // We send both error objs and undefineds in this streams. Only on the actual error msgs being emitted do we stop loading.
            setIsLoading(false);
          }
          subscriber.next(x);
        });
        return () => {
          subscription.unsubscribe();
        };
      }),
    [errors]
  );

  const callback: SubscriptionCallback<unknown> = useCallback(
    (err, json) => {
      if (err && isErrorSubscriptionResponse(err)) {
        errors.next(err);
      }
      if (json) {
        errors.next(undefined);
        subject.next(json);
      }
    },
    [subject, errors]
  );

  useEffect(() => {
    if (client == null || status !== WebSocketStatus.CONNECTED) {
      return;
    }

    if (registeredClientRef.current !== client) {
      // The client backing any existing registration is gone — a different instance now answers to
      // the same key/slot, and it has no idea our address was ever registered. Forget the old
      // registration so the logic below re-registers the (possibly unchanged) request against the new
      // client, instead of comparing request content and concluding there's nothing to do.
      activeRequest.current = null;
      registeredClientRef.current = client;
    }

    let requestStream: RequestStream[] | null = request as RequestStream[];

    // null-check so we don't do `request = [undefined]`
    if (request != null && !Array.isArray(request)) {
      // Eslint tells us to store the request in a useRef, but this works just fine
      requestStream = [request];
    }

    // Set request to `null` if there are no streams specified
    if (request != null && (request as object[]).length === 0) {
      requestStream = null;
    }

    // amend: have active subscription, request is set (but changed), and component is mounted
    if (
      requestStream != null &&
      hasSubscriber &&
      activeRequest.current != null &&
      JSON.stringify(requestStream) !== JSON.stringify(activeRequest.current)
    ) {
      setIsLoading(true);
      errors.next(undefined);
      client.updateSubscription(address, requestStream, callback);
      activeRequest.current = requestStream;
    }

    // register: have no active subscription, request is set and component is mounted
    else if (
      activeRequest.current == null &&
      requestStream != null &&
      hasSubscriber &&
      (!options.unsubscribeOnPageInvisibility || isPageVisible)
    ) {
      setIsLoading(true);
      client.registerSubscription(address, requestStream, callback, options);
      activeRequest.current = requestStream;
    }

    // cancel: have active subscription, but request is null
    else if (activeRequest.current != null && requestStream == null) {
      setIsLoading(false);
      errors.next(undefined);
      client.unregisterSubscription(address);
      activeRequest.current = null;
    }

    // cancel: do not have any active subscriptions, but there is an active request
    else if (!hasSubscriber && activeRequest.current != null) {
      activeRequest.current = null;
      errors.next(undefined);
      client.unregisterSubscription(address);
    }

    // unsubscribe on page invisibility
    else if (options.unsubscribeOnPageInvisibility && !isPageVisible && activeRequest.current != null) {
      activeRequest.current = null;
      errors.next(undefined);
      client.unregisterSubscription(address);
    }
  }, [address, status, client, request, callback, hasSubscriber, options, isPageVisible, errors]);

  useUnmount(() => {
    // client can be null here even with an active registration: the effect above returns early (without
    // clearing activeRequest) whenever client is null, so a named client torn down (e.g. CM's client on
    // API key rotation) while this component is still mounted leaves activeRequest set against a client
    // that no longer exists — unmounting during that gap must not crash trying to unregister from it.
    if (activeRequest.current != null) {
      activeRequest.current = null;
      client?.unregisterSubscription(address);
    }
  });

  useDebugValue(address, () => ({ address, activeRequest: activeRequest.current, hasSubscriber }));

  const nextPage = useDynamicCallback((options: NextPageOptions = {}) => {
    // client can be null here for the same reason it can be in useUnmount above — this is public API a
    // caller can invoke at any time, including before a named client has registered.
    client?.pageSubscription(address, options);
  });

  const push = useDynamicCallback((message: T) => {
    subject.next(message as SubscriptionResponse<T>);
  });

  return {
    isLoading,
    data: observable as unknown as Observable<T>,
    error: errorObservable as unknown as Observable<ErrorSubscriptionResponse | undefined>,
    push,
    nextPage,
  };
}
